import dayjs from "dayjs";
import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthenticationService } from "services/AuthenticationService";
import { BrokersService } from "services/BrokersService";
import { LoanStatusCode, LoanType, RoleCode } from "shared/helpers/Constants";
import { AuthActions } from "state/auth/Action";
import { ProfileActions } from "state/profile/Action";
import { ProfileState } from "state/profile/Reducer";
import { ProgressActions } from "state/progress/Action";
import { rndProgressState, sblProgressState } from "state/progress/Reducer";

interface SubItem {
  id: string;
  current: boolean;
  completed?: boolean;
}

interface ParentItem {
  id: string;
  parentCurrent: boolean;
  subItems?: SubItem[];
}

interface ProgressContextProps {
  currentProfile: ProfileState;
  isBroker: boolean;
  itemsProgress: ParentItem[];
  currentSectionId: string | null;
  handleCurrentById: (targetSubItemId: string) => void;
  handleCompleteAndNext: () => void;
  getCurrentSection: () => string | null;
  showSection: (sectionId: string) => boolean;
  syncProgressWithRoute: (pathname: string) => void;
  navigateToStep: (stepId: string) => void;
  handleUpdateUserLoan: (res: any) => void;
  handleReviewDetails: (res: any) => void;
  navigateToSectionStepHandler: (sectionId: string, stepNumber: number) => void;
  handleLoanStatus: (res: any) => void;
  getAllApplications: (pagination: any, brokerId: any, callback: any) => void;
}

const ProgressContext = createContext<ProgressContextProps | null>(null);

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error("useProgress must be used within a ProgressProvider");
  }
  return context;
};

export const getLoanTypeName = (loanType: number): string => {
  return loanType === LoanType.SBL ? "Secured Business Loan" : "R&D Loan";
};

export const getLoanTypeValue = (loanType: number): any => {
  return loanType === LoanType.SBL ? "sbl" : "rnd";
};

export const getApplicationStatus = (loanStatus: number): string => {
  switch (loanStatus) {
    case LoanStatusCode.PENDING_WITH_APPLICANT:
      return "Pending with Applicant";
    case LoanStatusCode.UNDER_REVIEW:
      return "Under Review";
    case LoanStatusCode.SUBMITTED:
      return "Submitted";
    case LoanStatusCode.APPROVED:
      return "Approved";
    case LoanStatusCode.REJECTED:
      return "Rejected";
    case LoanStatusCode.MORE_DOCUMENTS_REQUIRED:
      return "More Documents Required";
    case LoanStatusCode.NEED_CALIRIFICATION:
      return "Need Clarification";
    case LoanStatusCode.LOAN_DISBURSED:
      return "Loan Disbursed";
    case LoanStatusCode.CALL_BACK:
      return "Call Back";
    case LoanStatusCode.AMOUNT_REQUESTED_ABOVE_PROPERTY_VALUE:
      return "Amount Requested Above Property Value";
    default:
      return "-";
  }
};

export const sblRouteToStepMapping = {
  "/sbl/intro-details": "intro-details",
  "/sbl/intro-loan": "intro-loan",
  "/sbl/intro-business": "intro-business",
  "/sbl/sec-property": "sec-property",
  "/sbl/sec-liabilities": "sec-liabilities",
  "/sbl/verif-review": "verif-review",
  "/sbl/verif-directors": "verif-directors",
  "/sbl/verif-identity": "verif-identity",
  "/sbl/final-solicitor": "final-solicitor",
  "/sbl/final-bank": "final-bank",
  "/sbl/final-agreements": "final-agreements",
};

export const rndRouteToStepMapping = {
  "/rnd/intro-details": "intro-details",
  "/rnd/intro-loan": "intro-loan",
  "/rnd/review-approval": "review-approval",
  "/rnd/final-business": "final-business",
  "/rnd/final-directors": "final-directors",
  "/rnd/final-identity": "final-identity",
  "/rnd/final-accountant": "final-accountant",
  "/rnd/final-bank": "final-bank",
  "/rnd/final-agreements": "final-agreements",
};

const routeToStepMapping = {
  ...sblRouteToStepMapping,
  ...rndRouteToStepMapping,
};

const sblStepToRouteMapping = Object.fromEntries(
  Object.entries(sblRouteToStepMapping).map(([route, step]) => [step, route]),
);

const rndStepToRouteMapping = Object.fromEntries(
  Object.entries(rndRouteToStepMapping).map(([route, step]) => [step, route]),
);

export const ProgressProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const progressState = useSelector((state: any) => state.progressState);
  const profileState = useSelector((state: any) => state.profileState);
  const authInfo = useSelector((state: any) => state.authState);

  const getLoanType = useCallback((pathname: string): "sbl" | "rnd" | null => {
    if (pathname.startsWith("/sbl")) return "sbl";
    if (pathname.startsWith("/rnd")) return "rnd";
    return null;
  }, []);

  const currentLoanType =
    progressState.currentLoanType === null
      ? getLoanType(location.pathname)
      : progressState.currentLoanType;

  const itemsProgress: ParentItem[] =
    currentLoanType === "rnd"
      ? progressState.rndProgress
      : progressState.sblProgress;

  const currentProfile =
    currentLoanType === "rnd"
      ? profileState.rndProfile
      : profileState.sblProfile;

  const isBroker = authInfo.userRole === RoleCode.BROKER ? true : false;

  const isNavigatingRef = useRef(false);

  const getStepToRouteMapping = useCallback(
    (loanType: "sbl" | "rnd" | null) => {
      if (loanType === "rnd") return rndStepToRouteMapping;
      return sblStepToRouteMapping;
    },
    [],
  );

  const getStepIdFromPath = useCallback(
    (pathname: string) => {
      const loanType = getLoanType(pathname);
      if (!loanType) return null;

      const parts = pathname.split("/").filter(Boolean);
      const loanTypeIndex = parts.indexOf(loanType);
      if (loanTypeIndex === -1) return null;
      return parts[loanTypeIndex + 1] || null;
    },
    [getLoanType],
  );

  const getCurrentSection = useCallback((): string | null => {
    const currentParentIndex = itemsProgress.findIndex(
      (parent) => parent.parentCurrent === true,
    );

    if (currentParentIndex === -1) {
      return getStepIdFromPath(location.pathname);
    }

    const currentParent = itemsProgress[currentParentIndex];
    if (!currentParent.subItems || currentParent.subItems.length === 0) {
      return getStepIdFromPath(location.pathname);
    }

    const currentChild = currentParent.subItems.find(
      (child) => child.current === true,
    );

    if (!currentChild) return getStepIdFromPath(location.pathname);

    return currentChild.id;
  }, [location.pathname, itemsProgress, getStepIdFromPath]);

  const isSectionAccessible = useCallback(
    (targetId: string): boolean => {
      let targetParentIndex = -1;
      let targetChildIndex = -1;

      itemsProgress.forEach((parent, parentIndex) => {
        if (parent.subItems) {
          parent.subItems.forEach((child, childIndex) => {
            if (child.id === targetId) {
              targetParentIndex = parentIndex;
              targetChildIndex = childIndex;
            }
          });
        }
      });

      if (targetParentIndex === -1 || targetChildIndex === -1) return false;

      if (targetParentIndex === 0 && targetChildIndex === 0) return true;

      const targetParent = itemsProgress[targetParentIndex];

      if (targetChildIndex > 0) {
        const previousChild = targetParent.subItems![targetChildIndex - 1];
        return previousChild.completed || false;
      }

      if (targetParentIndex > 0) {
        const previousParent = itemsProgress[targetParentIndex - 1];
        if (previousParent.subItems && previousParent.subItems.length > 0) {
          return previousParent.subItems.every((subItem) => subItem.completed);
        }
      }

      return false;
    },
    [itemsProgress],
  );

  const handleCurrentById = useCallback(
    (targetSubItemId: string) => {
      const currentSection = getCurrentSection();
      if (currentSection === targetSubItemId) return;

      const updatedProgress = itemsProgress.map((parent) => {
        const hasTargetSubItem = parent.subItems?.some(
          (subItem) => subItem.id === targetSubItemId,
        );

        if (hasTargetSubItem) {
          return {
            ...parent,
            parentCurrent: true,
            subItems: parent.subItems?.map((subItem) => ({
              ...subItem,
              current: subItem.id === targetSubItemId,
            })),
          };
        } else {
          return {
            ...parent,
            parentCurrent: false,
            subItems:
              parent.subItems?.map((subItem) => ({
                ...subItem,
                current: false,
              })) || [],
          };
        }
      });

      if (currentLoanType === "rnd") {
        dispatch(ProgressActions.setRndProgress(updatedProgress));
      } else {
        dispatch(ProgressActions.setSblProgress(updatedProgress));
      }
    },
    [itemsProgress, getCurrentSection, dispatch, currentLoanType],
  );

  const syncProgressWithRoute = useCallback(
    (pathname: string) => {
      if (isNavigatingRef.current) return;

      const stepId =
        routeToStepMapping[pathname as keyof typeof routeToStepMapping];
      if (stepId && isSectionAccessible(stepId)) {
        handleCurrentById(stepId);
      }
    },
    [handleCurrentById, isSectionAccessible],
  );

  const navigateToStep = useCallback(
    (stepId: string) => {
      const loanType = getLoanType(location.pathname);
      const stepToRouteMapping = getStepToRouteMapping(loanType);
      const route = stepToRouteMapping[stepId];

      if (route && isSectionAccessible(stepId)) {
        isNavigatingRef.current = true;
        handleCurrentById(stepId);
        navigate(route);
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      }
    },
    [
      navigate,
      isSectionAccessible,
      handleCurrentById,
      location.pathname,
      getLoanType,
      getStepToRouteMapping,
    ],
  );

  const handleCompleteAndNext = useCallback(() => {
    const currentSectionId =
      getStepIdFromPath(location.pathname) || getCurrentSection();
    if (!currentSectionId) return;

    let nextItemId: string | null = null;
    let foundCurrent = false;

    for (const parent of itemsProgress) {
      if (parent.subItems) {
        for (const subItem of parent.subItems) {
          if (foundCurrent) {
            nextItemId = subItem.id;
            break;
          }
          if (subItem.id === currentSectionId) {
            foundCurrent = true;
          }
        }
        if (nextItemId) break;
      }
    }

    const updatedProgress = itemsProgress.map((parent) => {
      const updatedSubItems =
        parent.subItems?.map((subItem) => {
          if (subItem.id === currentSectionId) {
            return { ...subItem, completed: true, current: false };
          }
          if (subItem.id === nextItemId) {
            return { ...subItem, current: true };
          }
          return { ...subItem, current: false };
        }) || [];

      const allSubItemsCompleted =
        updatedSubItems.length > 0 &&
        updatedSubItems.every((subItem) => subItem.completed === true);

      return {
        ...parent,
        subItems: updatedSubItems,
        parentCurrent:
          updatedSubItems.some((item) => item.id === nextItemId) || false,
        parentCompleted: allSubItemsCompleted,
      };
    });

    if (currentLoanType === "rnd") {
      dispatch(ProgressActions.setRndProgress(updatedProgress));
    } else {
      dispatch(ProgressActions.setSblProgress(updatedProgress));
    }

    if (nextItemId) {
      const loanType = getLoanType(location.pathname);
      const stepToRouteMapping = getStepToRouteMapping(loanType);
      const route = stepToRouteMapping[nextItemId];

      if (route) {
        const isNextStepAccessible = checkAccessibilityWithProgress(
          nextItemId,
          updatedProgress,
        );
        if (isNextStepAccessible) {
          isNavigatingRef.current = true;
          navigate(route);
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 100);
        }
      }
    }
  }, [
    getCurrentSection,
    itemsProgress,
    dispatch,
    navigate,
    location.pathname,
    getLoanType,
    getStepToRouteMapping,
    getStepIdFromPath,
  ]);

  const checkAccessibilityWithProgress = useCallback(
    (targetId: string, progressState: ParentItem[]): boolean => {
      let targetParentIndex = -1;
      let targetChildIndex = -1;

      progressState.forEach((parent, parentIndex) => {
        if (parent.subItems) {
          parent.subItems.forEach((child, childIndex) => {
            if (child.id === targetId) {
              targetParentIndex = parentIndex;
              targetChildIndex = childIndex;
            }
          });
        }
      });

      if (targetParentIndex === -1 || targetChildIndex === -1) return false;

      if (targetParentIndex === 0 && targetChildIndex === 0) return true;

      const targetParent = progressState[targetParentIndex];

      if (targetChildIndex > 0) {
        const previousChild = targetParent.subItems![targetChildIndex - 1];
        return previousChild.completed || false;
      }

      if (targetParentIndex > 0) {
        const previousParent = progressState[targetParentIndex - 1];
        if (previousParent.subItems && previousParent.subItems.length > 0) {
          return previousParent.subItems.every((subItem) => subItem.completed);
        }
      }

      return false;
    },
    [],
  );

  const navigateToSectionStepHandler = useCallback(
    (sectionId: string, stepNumber: number) => {
      const loanType = getLoanType(location.pathname);
      navigate(`/${loanType}/${sectionId}/${stepNumber}`);
    },
    [navigate, location.pathname, getLoanType],
  );

  const showSection = useCallback(
    (sectionId: string): boolean => {
      return (
        getCurrentSection() === sectionId && isSectionAccessible(sectionId)
      );
    },
    [getCurrentSection, isSectionAccessible],
  );

  const handleUpdateUserLoan = (res) => {
    let data = res.data.data;
    const LoanType = data?.loanType;

    if (LoanType === 0) {
      dispatch(
        ProfileActions.setSblProfile({
          // Loan Details
          loadId: data.loanDetails?.loanId,
          expectedTime: data.loanDetails?.expectedTime,
          loanAmount: data.loanDetails?.loanAmount,
          loanPayBackOption: data.loanDetails?.loanPayBackOption,
          loanPurpose: data.loanDetails?.loanPurpose,
          loanTerm: data.loanDetails?.loanTerm,
          loanStatus: data.loanDetails?.loanStatus,
          repaymentMethod: data.loanDetails?.repaymentMethod,
          brokerCommissionRate: data.loanDetails?.brokerCommissionRate,
          expectedLoanDate: data.loanDetails?.expectedLoanDate,
          // Business Details
          abn: data.businessDetails?.abn,
          averageMonthlyTurnOver: data.businessDetails?.averageMonthlyTurnOver,
          totalBusinessAssets: data.businessDetails?.totalBusinessAssets,
          totalBusinessLiabilities:
            data.businessDetails?.totalBusinessLiabilities,
          entityAddress: data.businessDetails?.entityAddress,
          entityType: data.businessDetails?.entityType,
          isABNVerified: data.businessDetails?.isABNVerified,
          isMonthlyRepayment: data.businessDetails?.isMonthlyRepayment,
          isReviewConfirmed: data.businessDetails?.isReviewConfirmed,
          legalEntityType: data.businessDetails?.legalEntityType,
          registeredEntityName: data.businessDetails?.registeredEntityName,
          // Security Details
          isPropertyOwned: data.securityDetails?.isPropertyOwned,
          noOfProperties: data.securityDetails?.noOfProperties,
          securityId: data.securityDetails?.securityId,
          properties: data.securityDetails?.properties,
          // ---- Security Details properties
          estimatedPropertyAmount:
            data.securityDetails.properties[0]?.estimatedPropertyAmount || null,
          existingLoanAmount:
            data.securityDetails.properties[0]?.existingLoanAmount || null,
          existingLoanSettlementOption:
            data.securityDetails.properties[0]?.existingLoanSettlementOption ||
            "",
          isAnyCaveats: data.securityDetails.properties[0]?.isAnyCaveats,
          isAnyExistingLoan:
            data.securityDetails.properties[0]?.isAnyExistingLoan,
          propertyAddress:
            data.securityDetails.properties[0]?.propertyAddress || "",
          propertyType: data.securityDetails.properties[0]?.propertyType || "",
          isAllDataFilled: data.securityDetails.properties[0]?.isAllDataFilled,
          // Verification Details
          // -------- Review Details Payment
          paymentDetails: data?.paymentDetails,
          //--------- Stakeholders
          stakeholderDetails: data?.stakeholderDetails,
          // -------- Identity verification
          kycDetails: data?.kycDetails,
          kycId: data.kycDetails?.kycId,
          // Finalise

          BSB: data.bankDetails?.BSB,
          accountName: data.bankDetails?.accountName,
          accountNumber: data.bankDetails?.accountNumber,
          // Solicitors
          solicitorfirstName: data.solicitorDetails?.firstName,
          solicitorLastName: data.solicitorDetails?.lastName,
          solicitorBusinessName: data.solicitorDetails?.businessName,
          solicitorWorkEmailAddress: data.solicitorDetails?.email,
          //Signin agreement
          documentStatus: data?.documentStatus,
          signerStatus: data?.signerStatus,
          signedDocumentUrl: data?.signedDocumentUrl,
          signedDocumentName: data?.signedDocumentName,
          //Final status
          showNonEditableMessage: data?.showNonEditableMessage,
          isAllowedToEdit: data?.isAllowedToEdit,
        }),
      );
    }

    if (LoanType === 1) {
      dispatch(
        ProfileActions.setRndProfile({
          //Loan
          loadId: data.rndLoanDetails?.loanId,
          loanAmount: data.rndLoanDetails?.loanAmount,
          annualTurnover: data.rndLoanDetails?.annualTurnover,
          estimatedExpenditureAmount:
            data.rndLoanDetails?.estimatedExpenditureAmount,
          estimatedTaxFilingDate: data.rndLoanDetails?.estimatedTaxFilingDate,
          hasATOClaims: data.rndLoanDetails?.hasATOClaims,
          hasTaxIncentive: data.rndLoanDetails?.hasTaxIncentive,
          taxYear: data.rndLoanDetails?.taxYear,
          loanTerm: data.rndLoanDetails?.loanTerm,
          brokerCommissionRate: data.rndLoanDetails?.brokerCommissionRate,
          //Conditional Approval
          paymentDetails: data?.paymentDetails,
          //Business
          abn: data.businessDetails.abn,
          companyAddress: data.businessDetails?.companyAddress,
          entityAddress: data.businessDetails?.entityAddress,
          entityType: data.businessDetails?.entityType,
          auRegDate: data.businessDetails?.lastRegistrationDate,
          registeredEntityName: data.businessDetails?.registeredEntityName,
          //--------- Stakeholders
          stakeholderDetails: data?.stakeholderDetails,
          // -------- Identity verification
          kycDetails: data?.kycDetails,
          kycId: data.kycDetails?.kycId,
          //Accountant Details
          accountantBusinessName: data.accountantDetails?.businessName,
          accountantFirstName: data.accountantDetails?.firstName,
          accountantLastName: data.accountantDetails?.lastName,
          accountantWorkEmailAddress: data.accountantDetails?.email,
          //Bank Details
          BSB: data.bankDetails?.BSB,
          accountName: data.bankDetails?.accountName,
          accountNumber: data.bankDetails?.accountNumber,
          //Final status
          showNonEditableMessage: data?.showNonEditableMessage,
          isAllowedToEdit: data?.isAllowedToEdit,
        }),
      );
    }
  };

  const handleReviewDetails = useCallback(
    (res: any) => {
      const data = res.data.data;
      const LoanType = data?.loanType;

      const progressLevelsSBL = [
        {
          condition: () =>
            data.loanDetails?.expectedTime &&
            data.businessDetails?.abn &&
            data.securityDetails?.isPropertyOwned &&
            data.paymentDetails?.paidAt &&
            data?.stakeholderDetails?.length > 0 &&
            data.kycDetails.isKYCCompleted === 2 &&
            data.solicitorDetails.email &&
            data.bankDetails.BSB,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "introduction", subItemId: "intro-business" },
            { parentId: "security", subItemId: "sec-property" },
            { parentId: "security", subItemId: "sec-liabilities" },
            { parentId: "verification", subItemId: "verif-review" },
            { parentId: "verification", subItemId: "verif-directors" },
            { parentId: "verification", subItemId: "verif-identity" },
            { parentId: "finalise", subItemId: "final-solicitor" },
            { parentId: "finalise", subItemId: "final-bank" },
          ],
          completedParents: ["introduction", "security", "verification"],
          nextStep: {
            parentId: "finalise",
            subItemId: "final-agreements",
          },
        },
        {
          condition: () =>
            data.loanDetails?.expectedTime &&
            data.businessDetails?.abn &&
            data.securityDetails?.isPropertyOwned &&
            data.paymentDetails?.paidAt &&
            data?.stakeholderDetails?.length > 0 &&
            data.kycDetails.isKYCCompleted === 2 &&
            data.solicitorDetails.email,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "introduction", subItemId: "intro-business" },
            { parentId: "security", subItemId: "sec-property" },
            { parentId: "security", subItemId: "sec-liabilities" },
            { parentId: "verification", subItemId: "verif-review" },
            { parentId: "verification", subItemId: "verif-directors" },
            { parentId: "verification", subItemId: "verif-identity" },
            { parentId: "finalise", subItemId: "final-solicitor" },
          ],
          completedParents: ["introduction", "security", "verification"],
          nextStep: {
            parentId: "finalise",
            subItemId: "final-bank",
          },
        },
        {
          condition: () =>
            data.loanDetails?.expectedTime &&
            data.businessDetails?.abn &&
            data.securityDetails?.isPropertyOwned &&
            data.paymentDetails?.paidAt &&
            data?.stakeholderDetails?.length > 0 &&
            data.kycDetails.isKYCCompleted === 2,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "introduction", subItemId: "intro-business" },
            { parentId: "security", subItemId: "sec-property" },
            { parentId: "security", subItemId: "sec-liabilities" },
            { parentId: "verification", subItemId: "verif-review" },
            { parentId: "verification", subItemId: "verif-directors" },
            { parentId: "verification", subItemId: "verif-identity" },
          ],
          completedParents: ["introduction", "security", "verification"],
          nextStep: {
            parentId: "finalise",
            subItemId: "final-solicitor",
          },
        },
        {
          condition: () =>
            data.loanDetails?.expectedTime &&
            data.businessDetails?.abn &&
            data.securityDetails?.isPropertyOwned &&
            data.paymentDetails?.paidAt &&
            data?.stakeholderDetails?.length > 0,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "introduction", subItemId: "intro-business" },
            { parentId: "security", subItemId: "sec-property" },
            { parentId: "security", subItemId: "sec-liabilities" },
            { parentId: "verification", subItemId: "verif-review" },
            { parentId: "verification", subItemId: "verif-directors" },
          ],
          completedParents: ["introduction", "security"],
          nextStep: {
            parentId: "verification",
            subItemId: "verif-identity",
          },
        },
        {
          condition: () =>
            data.loanDetails?.expectedTime &&
            data.businessDetails?.abn &&
            data.securityDetails?.isPropertyOwned &&
            data.paymentDetails?.paidAt,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "introduction", subItemId: "intro-business" },
            { parentId: "security", subItemId: "sec-property" },
            { parentId: "security", subItemId: "sec-liabilities" },
            { parentId: "verification", subItemId: "verif-review" },
          ],
          completedParents: ["introduction", "security"],
          nextStep: {
            parentId: "verification",
            subItemId: "verif-directors",
          },
        },
        {
          condition: () =>
            data.loanDetails?.expectedTime &&
            data.businessDetails?.abn &&
            data.securityDetails?.isPropertyOwned,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "introduction", subItemId: "intro-business" },
            { parentId: "security", subItemId: "sec-property" },
            { parentId: "security", subItemId: "sec-liabilities" },
          ],
          completedParents: ["introduction", "security"],
          nextStep: {
            parentId: "verification",
            subItemId: "verif-review",
          },
        },
        {
          condition: () =>
            data.loanDetails?.expectedTime && data.businessDetails?.abn,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "introduction", subItemId: "intro-business" },
          ],
          completedParents: ["introduction"],
          nextStep: { parentId: "security", subItemId: "sec-property" },
        },
        {
          condition: () => data.loanDetails?.expectedTime,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
          ],
          completedParents: [""],
          nextStep: { parentId: "introduction", subItemId: "intro-business" },
        },
        {
          condition: () => true,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
          ],
          completedParents: [],
          nextStep: { parentId: "introduction", subItemId: "intro-loan" },
        },
      ];

      const progressLevelRND = [
        {
          condition: () =>
            data.rndLoanDetails?.hasATOClaims &&
            data.paymentDetails?.paymentStatus &&
            data.businessDetails?.abn &&
            data.stakeholderDetails?.length > 0 &&
            data.kycDetails?.isKYCCompleted === 2 &&
            data.accountantDetails?.businessName &&
            data.bankDetails?.BSB,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "review-details", subItemId: "review-approval" },
            { parentId: "finalise", subItemId: "final-business" },
            { parentId: "finalise", subItemId: "final-directors" },
            { parentId: "finalise", subItemId: "final-identity" },
            { parentId: "finalise", subItemId: "final-accountant" },
            { parentId: "finalise", subItemId: "final-bank" },
          ],
          completedParents: ["introduction", "review-details"],
          nextStep: {
            parentId: "finalise",
            subItemId: "final-agreements",
          },
        },
        {
          condition: () =>
            data.rndLoanDetails?.hasATOClaims &&
            data.paymentDetails?.paymentStatus &&
            data.businessDetails?.abn &&
            data.stakeholderDetails?.length > 0 &&
            data.kycDetails?.isKYCCompleted === 2 &&
            data.accountantDetails?.businessName,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "review-details", subItemId: "review-approval" },
            { parentId: "finalise", subItemId: "final-business" },
            { parentId: "finalise", subItemId: "final-directors" },
            { parentId: "finalise", subItemId: "final-identity" },
            { parentId: "finalise", subItemId: "final-accountant" },
          ],
          completedParents: ["introduction", "review-details"],
          nextStep: {
            parentId: "finalise",
            subItemId: "final-bank",
          },
        },
        {
          condition: () =>
            data.rndLoanDetails?.hasATOClaims &&
            data.paymentDetails?.paymentStatus &&
            data.businessDetails?.abn &&
            data.stakeholderDetails?.length > 0 &&
            data.kycDetails?.isKYCCompleted === 2,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "review-details", subItemId: "review-approval" },
            { parentId: "finalise", subItemId: "final-business" },
            { parentId: "finalise", subItemId: "final-directors" },
            { parentId: "finalise", subItemId: "final-identity" },
          ],
          completedParents: ["introduction", "review-details"],
          nextStep: {
            parentId: "finalise",
            subItemId: "final-accountant",
          },
        },
        {
          condition: () =>
            data.rndLoanDetails?.hasATOClaims &&
            data.paymentDetails?.paymentStatus &&
            data.businessDetails?.abn &&
            data.stakeholderDetails?.length > 0,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "review-details", subItemId: "review-approval" },
            { parentId: "finalise", subItemId: "final-business" },
            { parentId: "finalise", subItemId: "final-directors" },
          ],
          completedParents: ["introduction", "review-details"],
          nextStep: {
            parentId: "finalise",
            subItemId: "final-identity",
          },
        },
        {
          condition: () =>
            data.rndLoanDetails?.hasATOClaims &&
            data.paymentDetails?.paymentStatus &&
            data.businessDetails?.abn,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "review-details", subItemId: "review-approval" },
            { parentId: "finalise", subItemId: "final-business" },
          ],
          completedParents: ["introduction", "review-details"],
          nextStep: {
            parentId: "finalise",
            subItemId: "final-directors",
          },
        },
        {
          condition: () =>
            data.rndLoanDetails?.hasATOClaims &&
            data.paymentDetails?.paymentStatus,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
            { parentId: "review-details", subItemId: "review-approval" },
          ],
          completedParents: ["introduction", "review-details"],
          nextStep: { parentId: "finalise", subItemId: "final-business" },
        },
        {
          condition: () => data.rndLoanDetails?.hasATOClaims,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
            { parentId: "introduction", subItemId: "intro-loan" },
          ],
          completedParents: ["introduction"],
          nextStep: {
            parentId: "review-details",
            subItemId: "review-approval",
          },
        },
        {
          condition: () => true,
          completedSteps: [
            { parentId: "introduction", subItemId: "intro-details" },
          ],
          completedParents: [],
          nextStep: { parentId: "introduction", subItemId: "intro-loan" },
        },
      ];

      const progressLevels =
        LoanType === 0 ? progressLevelsSBL : progressLevelRND;

      const currentLevel = progressLevels.find((level) => level.condition());

      if (!currentLevel) return;
      let updatedProgress =
        LoanType === 0 ? sblProgressState : rndProgressState;
      currentLevel.completedSteps.forEach(({ parentId, subItemId }) => {
        updatedProgress = updatedProgress.map((parent) => {
          if (parent.id === parentId) {
            return {
              ...parent,
              subItems: parent.subItems?.map((subItem) => ({
                ...subItem,
                completed: subItem.id === subItemId ? true : subItem.completed,
              })),
            };
          }
          return parent;
        });
      });

      currentLevel.completedParents.forEach((parentId) => {
        if (parentId) {
          updatedProgress = updatedProgress.map((parent) => {
            if (parent.id === parentId) {
              return {
                ...parent,
                parentCompleted: true,
              };
            }
            return parent;
          });
        }
      });

      updatedProgress = updatedProgress.map((parent) => {
        const hasTargetSubItem = parent.subItems?.some(
          (subItem) => subItem.id === currentLevel.nextStep.subItemId,
        );

        if (hasTargetSubItem) {
          return {
            ...parent,
            parentCurrent: true,
            subItems: parent.subItems?.map((subItem) => ({
              ...subItem,
              current: subItem.id === currentLevel.nextStep.subItemId,
            })),
          };
        } else {
          return {
            ...parent,
            parentCurrent: false,
            subItems:
              parent.subItems?.map((subItem) => ({
                ...subItem,
                current: false,
              })) || [],
          };
        }
      });

      if (LoanType === 0) {
        dispatch(ProgressActions.setSblProgress(updatedProgress));
      }
      if (LoanType === 1) {
        dispatch(ProgressActions.setRndProgress(updatedProgress));
      }

      const loanType = getLoanTypeValue(LoanType);
      const stepToRouteMapping = getStepToRouteMapping(loanType);
      const nextStepRoute = stepToRouteMapping[currentLevel.nextStep.subItemId];

      if (nextStepRoute) {
        isNavigatingRef.current = true;
        navigate(nextStepRoute);
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      }
    },
    [
      itemsProgress,
      dispatch,
      navigate,
      location.pathname,
      getLoanType,
      getStepToRouteMapping,
    ],
  );

  const handleLoanStatus = (callback) => {
    AuthenticationService.getLoanStatus()
      .then((response) => {
        const data = response.data.data;
        const transformedRows = data.loans.map((loan) => ({
          id: loan.loanId,
          loanNumber: loan.loanNumber,
          loanType: getLoanTypeName(loan.loanType),
          value: getLoanTypeValue(loan.loanType),
          loanStatus: getApplicationStatus(loan.loanStatus),
          loanActualType: loan.loanType,
          loanApplicationSubmittedDate:
            (loan?.loanApplicationSubmittedDate &&
              dayjs(loan?.loanApplicationSubmittedDate).format("YYYY-MM-DD")) ||
            "-",
          email: loan.email,
        }));

        dispatch(
          AuthActions.setAuthData({
            rowLoanStatus: transformedRows,
          }),
        );
        if (transformedRows.length > 0) {
          callback(transformedRows);
        }
      })
      .catch((error) => {});
  };

  const getAllApplications = (pagination, brokerId, callback) => {
    BrokersService.getAllApplcations(
      pagination.offset,
      pagination.limit,
      brokerId,
    )
      .then((res) => {
        const data = res.data.data;

        const transformedRows = data.loans.map((loan, index) => ({
          id: loan.loanId,
          loanNumber: loan.loanNumber,
          loanType: getLoanTypeName(loan.loanType) || "-",
          value: getLoanTypeValue(loan.loanType) || "-",
          loanStatus: getApplicationStatus(loan.loanStatus) || "-",
          loanActualType: loan.loanType,
          email: loan.email,
          brokerEmail: loan.brokerEmail,
          loanApplicationSubmittedDate:
            (loan?.loanApplicationSubmittedDate &&
              dayjs(loan?.loanApplicationSubmittedDate).format("YYYY-MM-DD")) ||
            "-",
        }));
        dispatch(
          AuthActions.setAuthData({
            rowLoanStatus: transformedRows,
          }),
        );
        if (transformedRows.length > 0) {
          callback(transformedRows);
        }
      })
      .catch((error: any) => {
        console.log(error);
      });
  };

  const value: ProgressContextProps = {
    currentProfile,
    isBroker,
    itemsProgress,
    currentSectionId: getCurrentSection(),
    handleCurrentById,
    handleCompleteAndNext,
    getCurrentSection,
    showSection,
    syncProgressWithRoute,
    navigateToStep,
    handleUpdateUserLoan,
    handleReviewDetails,
    navigateToSectionStepHandler,
    handleLoanStatus,
    getAllApplications,
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};
